import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { PaziaFormButton, PaziaInput, PaziaLoader, PaziaTextarea } from "components/atoms";
import { useShopSeoSettings, shopApi, SeoSettings } from "api/shop";
import { useToaster } from "utils";
import { useEffect, useState } from "react";

const FORM_NAMES = {
  ORGANIZATION_NAME: "organizationName",
  ALTERNATE_NAME: "alternateName",
  SITE_NAME: "siteName",
  LOGO_URL: "logoUrl",
  SITE_DESCRIPTION: "siteDescription",
  GOOGLE_SITE_VERIFICATION_CODE: "googleSiteVerificationCode",
} as const;

const URL_REGEX = /^https?:\/\/[^\s]+$/i;

const schema = yup
  .object({
    [FORM_NAMES.ORGANIZATION_NAME]: yup.string().trim().required("Pełna nazwa organizacji jest wymagana"),
    [FORM_NAMES.ALTERNATE_NAME]: yup.string().trim().required("Krótka nazwa marki jest wymagana"),
    [FORM_NAMES.SITE_NAME]: yup.string().trim().required("Nazwa witryny jest wymagana"),
    [FORM_NAMES.LOGO_URL]: yup
      .string()
      .trim()
      .required("URL logo jest wymagany")
      .matches(URL_REGEX, "Podaj poprawny absolutny URL (http:// lub https://)"),
    [FORM_NAMES.SITE_DESCRIPTION]: yup.string().trim().required("Opis strony jest wymagany"),
    [FORM_NAMES.GOOGLE_SITE_VERIFICATION_CODE]: yup.string().trim(),
  })
  .required();

interface FormValues {
  organizationName: string;
  alternateName: string;
  siteName: string;
  logoUrl: string;
  siteDescription: string;
  googleSiteVerificationCode: string;
}

export const ShopSeoSettingsForm = () => {
  const { seoSettings, isLoading, invalidate } = useShopSeoSettings();
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      organizationName: seoSettings?.organizationName ?? "",
      alternateName: seoSettings?.alternateName ?? "",
      siteName: seoSettings?.siteName ?? "",
      logoUrl: seoSettings?.logoUrl ?? "",
      siteDescription: seoSettings?.siteDescription ?? "",
      googleSiteVerificationCode: seoSettings?.googleSiteVerificationCode ?? "",
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    if (seoSettings) {
      reset({
        organizationName: seoSettings.organizationName ?? "",
        alternateName: seoSettings.alternateName ?? "",
        siteName: seoSettings.siteName ?? "",
        logoUrl: seoSettings.logoUrl ?? "",
        siteDescription: seoSettings.siteDescription ?? "",
        googleSiteVerificationCode: seoSettings.googleSiteVerificationCode ?? "",
      });
    }
  }, [seoSettings, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      const trimmedGoogleSiteVerificationCode = data.googleSiteVerificationCode.trim();
      const payload: SeoSettings = {
        organizationName: data.organizationName.trim(),
        alternateName: data.alternateName.trim(),
        siteName: data.siteName.trim(),
        logoUrl: data.logoUrl.trim(),
        siteDescription: data.siteDescription.trim(),
        googleSiteVerificationCode: trimmedGoogleSiteVerificationCode === "" ? undefined : trimmedGoogleSiteVerificationCode,
      };
      await shopApi.updateSeoSettings(payload);
      showSuccess("Zapisano ustawienia SEO");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać ustawień SEO");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-2xl gap-6">
        <div>
          <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Ustawienia SEO</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Globalne dane wykorzystywane w meta tagach oraz danych strukturalnych JSON-LD.
          </p>
        </div>

        <div className="grid gap-1">
          <PaziaInput
            name={FORM_NAMES.ORGANIZATION_NAME}
            label="Pełna nazwa organizacji"
            placeholder="np. Pazia Spółka z ograniczoną odpowiedzialnością"
            required
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Używana w danych strukturalnych JSON-LD jako pole name organizacji.
          </p>
        </div>

        <div className="grid gap-1">
          <PaziaInput
            name={FORM_NAMES.ALTERNATE_NAME}
            label="Krótka nazwa marki"
            placeholder="np. Pazia"
            required
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Używana w danych strukturalnych JSON-LD jako alternateName organizacji.
          </p>
        </div>

        <div className="grid gap-1">
          <PaziaInput
            name={FORM_NAMES.SITE_NAME}
            label="Nazwa witryny (og:site_name)"
            placeholder="np. Szkolenia GW"
            required
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Wykorzystywana jako wartość og:site_name w meta tagach Open Graph. Wpływa na to, jak nazwa Twojej witryny jest prezentowana w udostępnieniach społecznościowych.
          </p>
        </div>

        <div className="grid gap-1">
          <PaziaInput
            name={FORM_NAMES.LOGO_URL}
            label="URL logo (kanoniczne)"
            placeholder="https://example.com/logo.png"
            required
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Absolutny URL logo, używany w JSON-LD organizacji oraz jako fallback Open Graph.
          </p>
        </div>

        <div className="grid gap-1">
          <PaziaTextarea
            name={FORM_NAMES.SITE_DESCRIPTION}
            label="Opis strony (meta description fallback)"
            placeholder="Krótki opis serwisu wykorzystywany na stronach bez własnego meta description."
            rows={3}
            required
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Wykorzystywany jako domyślny meta description oraz description w JSON-LD WebSite.
          </p>
        </div>

        <div className="grid gap-1">
          <PaziaInput
            name={FORM_NAMES.GOOGLE_SITE_VERIFICATION_CODE}
            label="Kod weryfikacyjny Google Search Console"
            placeholder="abcDEF123_-xyz"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Wartość atrybutu content meta tagu google-site-verification dodawana w nagłówku strony. Pozostaw puste, aby nie renderować tagu.
          </p>
        </div>

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving || !isValid} />
        </div>
      </form>
    </FormProvider>
  );
};
